'use client'

import type React from 'react'
import { type ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react'
import videos from '../data/videos'
import type { UserPrefs } from '../types/UserPrefs'
import { isAV1PlaybackSmooth } from '../utils/videoUtils'

// Local storage key for user preferences
const USER_PREFS_STORAGE_KEY = 'te-rarawa-user-prefs'

// Default user preferences
const defaultUserPrefs: UserPrefs = {
  language: 'english',
  subtitlesEnabled: true,
  subtitleLanguage: 'english',
  audioEnabled: true,
  useAV1: false,
  codecDetectionComplete: false,
  isMobile: false,
  hasGyro: false,
  isVR: false,
  supportsVR: false,
  useGyro: false,
  debugMode: false,
}

// Create context with default values and update function
interface UserPrefsContextType {
  userPrefs: UserPrefs
  updateUserPrefs: (newPrefs: Partial<UserPrefs>) => void
  resetUserPrefs: () => void
  setGyroPermission: (granted: boolean) => void
  copyHelper: (englishText: string, maoriText?: string, defaultText?: string) => string
}

const UserPrefsContext = createContext<UserPrefsContextType | undefined>(undefined)

// Provider component
export const UserPrefsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Initialize state with saved preferences or defaults
  const [userPrefs, setUserPrefs] = useState<UserPrefs>(() => ({
    ...defaultUserPrefs,
  }))

  // Detect debug mode - wrapped in useCallback to prevent re-creation on every render
  const detectDebugMode = useCallback(() => {
    if (typeof window !== 'undefined') {
      return window.location.hash.includes('debug') || process.env.NODE_ENV !== 'production'
    }
    return false
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return

    try {
      const savedPrefs = localStorage.getItem(USER_PREFS_STORAGE_KEY)
      if (savedPrefs) {
        const parsedPrefs = JSON.parse(savedPrefs) as Partial<UserPrefs>
        setUserPrefs((prev) => ({
          ...prev,
          ...parsedPrefs,
        }))
      }
    } catch (error) {
      console.error('Error loading user preferences from localStorage:', error)
    }
  }, [])

  // Detect device capabilities on mount
  useEffect(() => {
    // Detect if user is on mobile
    const detectMobile = () => {
      const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent || navigator.vendor : ''
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)
      return isMobileDevice
    }

    const gyroDetected = (evt: DeviceOrientationEvent) => {
      if (evt && (evt.beta !== null || evt.gamma !== null || evt.alpha !== null)) {
        console.log(['Gyroscope detected:', evt], 'preferences')
        setUserPrefs((prev) => ({ ...prev, hasGyro: true, useGyro: prev.useGyro }))
      }
    }

    // Detect if device has gyroscope (iOS/Safari needs permission)
    const detectGyro = async () => {
      if (typeof window !== 'undefined') {
        if ('Gyroscope' in window) {
          // @ts-expect-error check for below is above
          const gyro = new window.Gyroscope({ frequency: 60 })
          gyro.start()
          if (gyro.hasReading) {
            console.log('Detected gyro from Gyroscope API', 'preferences')
            gyro.stop()
            return true
          }

          gyro.stop()
        }

        try {
          const qResult = await navigator.permissions.query({ name: 'gyroscope' as PermissionName })
          if (qResult.state === 'granted' || qResult.state === 'prompt') {
            // Gyroscope is available and permission granted
            window.addEventListener('deviceorientation', gyroDetected, { once: true })

            return false // We can't assume there is a gyro
          }
        } catch {}

        if (
          'requestPermission' in DeviceOrientationEvent &&
          typeof DeviceOrientationEvent.requestPermission === 'function'
        ) {
          // iOS 13+ requires permission
          return true
        }
      }

      return false

      //   const checkGyro = async (resolve: (value: boolean) => void) => {
      //     if (window.DeviceOrientationEvent) {
      //       // iOS 13+ requires permission
      //       if (
      //         'requestPermission' in DeviceOrientationEvent &&
      //         typeof DeviceOrientationEvent.requestPermission === 'function'
      //       ) {
      //         try {
      //           const permission = await DeviceOrientationEvent.requestPermission()
      //           if (permission !== 'granted') {
      //             resolve(false)
      //             return
      //           }
      //         } catch {
      //           resolve(false)
      //           return
      //         }
      //       }
      //       const deviceOrientationListener = (event: DeviceOrientationEvent) => {
      //         if (event.beta !== null || event.gamma !== null || event.alpha !== null) {
      //           window.removeEventListener('deviceorientation', deviceOrientationListener)
      //           resolve(true)
      //         }
      //       }
      //       setTimeout(() => {
      //         window.removeEventListener('deviceorientation', deviceOrientationListener)
      //         resolve(false)
      //       }, 1500)
      //       window.addEventListener('deviceorientation', deviceOrientationListener, { once: true })
      //     } else {
      //       resolve(false)
      //     }
      //   }
      //   return new Promise<boolean>((resolve) => {
      //     checkGyro(resolve)
      //   })
    }

    // Detect if device supports VR (WebXR API)
    const detectSupportsVR = async () => {
      if (typeof navigator !== 'undefined' && 'xr' in navigator) {
        try {
          // @ts-ignore
          const isSupported = await navigator.xr?.isSessionSupported?.('immersive-vr')
          return !!isSupported
        } catch {
          return false
        }
      }
      return false
    }

    // Update user preferences with detected capabilities
    const updateDeviceCapabilities = async () => {
      const isMobile = detectMobile()
      const hasGyro = await detectGyro()
      const supportsVR = await detectSupportsVR()
      const debugMode = detectDebugMode()
      const useAV1 = await isAV1PlaybackSmooth()
      setUserPrefs((prev) => {
        const newPrefs = {
          ...prev,
          isMobile,
          hasGyro,
          supportsVR,
          debugMode,
          useAV1,
          codecDetectionComplete: true, // Mark codec detection as complete
          // useGyro will be set by setGyroPermission
        }
        console.log(['Initial device capabilities detected:', newPrefs], 'preferences')
        return newPrefs
      })
    }

    // Only run in browser environment
    if (typeof window !== 'undefined') {
      updateDeviceCapabilities()
    }

    return () => {
      console.log('UserPrefsContext: Cleanup - removing deviceorientation listener', 'preferences')
      window.removeEventListener('deviceorientation', gyroDetected)
    }
  }, [detectDebugMode])

  // Listen for hash changes to enable/disable debug mode
  useEffect(() => {
    const handleHashChange = () => {
      const debugMode = detectDebugMode()
      setUserPrefs((prev) => {
        if (prev.debugMode !== debugMode) {
          console.log(['Debug mode changed:', debugMode], 'preferences')
          return { ...prev, debugMode }
        }
        return prev
      })
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('hashchange', handleHashChange)
      return () => {
        window.removeEventListener('hashchange', handleHashChange)
      }
    }
  }, [detectDebugMode])

  // Save preferences to localStorage whenever they change
  useEffect(() => {
    if (typeof window !== 'undefined') {
      console.log(['UserPrefsContext: User preferences updated:', userPrefs], 'userPrefs')
      try {
        localStorage.setItem(USER_PREFS_STORAGE_KEY, JSON.stringify(userPrefs))
      } catch (error) {
        console.error(['Error saving user preferences to localStorage:', error], 'preferences')
      }

      // Send updated preferences to service worker
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'UPDATE_USER_PREFS',
          payload: {
            userPrefs: {
              isMobile: userPrefs.isMobile,
              useAV1: userPrefs.useAV1,
              isIOS:
                /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                (navigator.userAgent.includes('Macintosh') && 'ontouchend' in document),
              subtitlesEnabled: userPrefs.subtitlesEnabled,
              language: userPrefs.language,
            },
            // Include chapters so service worker can preload new language audio
            chapters: videos,
          },
        })
      }
    }
  }, [userPrefs])

  // Update user preferences
  const updateUserPrefs = (newPrefs: Partial<UserPrefs>) => {
    setUserPrefs((prevPrefs) => ({
      ...prevPrefs,
      ...newPrefs,
    }))
  }

  // Set gyro permission and update useGyro and hasGyro (always run detection/permission on toggle)
  const setGyroPermission = async (granted: boolean) => {
    let hasGyro = false
    if (typeof window !== 'undefined' && window.DeviceOrientationEvent) {
      // iOS 13+ requires permission
      if (
        'requestPermission' in DeviceOrientationEvent &&
        typeof DeviceOrientationEvent.requestPermission === 'function'
      ) {
        try {
          const permission = await DeviceOrientationEvent.requestPermission()
          if (permission !== 'granted') {
            hasGyro = false
            setUserPrefs((prev) => ({
              ...prev,
              hasGyro,
              useGyro: granted,
            }))
            return
          }
        } catch {
          hasGyro = false
          setUserPrefs((prev) => ({
            ...prev,
            hasGyro,
            useGyro: granted,
          }))
          return
        }
      }
      // Listen for deviceorientation event
      hasGyro = await new Promise<boolean>((resolve) => {
        const listener = (event: DeviceOrientationEvent) => {
          if (event.beta !== null || event.gamma !== null || event.alpha !== null) {
            window.removeEventListener('deviceorientation', listener)
            resolve(true)
          }
        }
        setTimeout(() => {
          window.removeEventListener('deviceorientation', listener)
          resolve(false)
        }, 1000)
        window.addEventListener('deviceorientation', listener, { once: true })
      })
    }
    setUserPrefs((prev) => ({
      ...prev,
      hasGyro,
      useGyro: !!(hasGyro && granted),
    }))
  }

  // Reset user preferences to defaults
  const resetUserPrefs = () => {
    const { isMobile, hasGyro, supportsVR, useGyro, debugMode } = userPrefs
    setUserPrefs({ ...defaultUserPrefs, isMobile, hasGyro, supportsVR, useGyro, debugMode })

    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem(USER_PREFS_STORAGE_KEY)
      } catch (error) {
        console.error('Error removing user preferences from localStorage:', error)
      }
    }
  }

  const copyHelper = (englishText: string, maoriText?: string, defaultText = ''): string => {
    if (userPrefs.language === 'english') {
      return englishText || defaultText
    }

    if (userPrefs.language === 'maori') {
      return maoriText || englishText || defaultText
    }

    return defaultText
  }

  return (
    <UserPrefsContext.Provider value={{ userPrefs, updateUserPrefs, resetUserPrefs, setGyroPermission, copyHelper }}>
      {children}
    </UserPrefsContext.Provider>
  )
}

// Custom hook for using the context
export const useUserPrefs = (): UserPrefsContextType => {
  const context = useContext(UserPrefsContext)
  if (context === undefined) {
    throw new Error('useUserPrefs must be used within a UserPrefsProvider')
  }
  return context
}
